import { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Plus, Loader2, Truck, Phone, Mail, MapPin, Search, ChevronDown, Sparkles, Pencil, TrendingDown, Trophy, ShieldCheck, ShieldAlert,
} from "lucide-react";
import api from "../lib/api";
import SupplierDialog from "../components/suppliers/SupplierDialog";
import SupplierAIDialog from "../components/suppliers/SupplierAIDialog";
import SupplierScorecardChip from "../components/suppliers/SupplierScorecardChip";
import SupplierScorecardBanner from "../components/suppliers/SupplierScorecardBanner";
import AlternativeSuppliersDialog from "../components/suppliers/AlternativeSuppliersDialog";
import { SUPPLIER_CATEGORIES, getCategoryMeta } from "../components/suppliers/categories";
import { PageHeader } from "../components/layout/PageHeader";
import { PageSkeleton } from "../components/layout/PageSkeleton";

const normalize = (s) =>
  (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const formatCurrency = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function SuppliersPage() {
  const { currentRestaurant } = useOutletContext();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name_asc");  // default: A→Z
  const [onlyEvaluated, setOnlyEvaluated] = useState(false);
  const [manualDialog, setManualDialog] = useState(false);
  const [aiDialog, setAIDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [savingsMap, setSavingsMap] = useState({});  // supplier_id -> savings entry
  const [savingsLoading, setSavingsLoading] = useState(false);
  const [savingsMeta, setSavingsMeta] = useState(null); // {total_savings, ingredients_analyzed}
  const [scorecardData, setScorecardData] = useState(null);
  const [scorecardMap, setScorecardMap] = useState({}); // supplier_id -> scorecard entry
  const [altSupplier, setAltSupplier] = useState(null);

  const fetchSuppliers = useCallback(async () => {
    try {
      const data = await api.getSuppliers(currentRestaurant.restaurant_id);
      setSuppliers(data);
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
    } finally {
      setLoading(false);
    }
  }, [currentRestaurant]);

  const fetchSavings = useCallback(async () => {
    if (!currentRestaurant?.restaurant_id) return;
    setSavingsLoading(true);
    try {
      const res = await api.getSuppliersSavingsRanking(currentRestaurant.restaurant_id, 90);
      const map = {};
      (res.ranking || []).forEach(r => { if (r.supplier_id) map[r.supplier_id] = r; });
      setSavingsMap(map);
      setSavingsMeta({
        total_savings: res.total_savings || 0,
        ingredients_analyzed: res.ingredients_analyzed || 0,
        suppliers_analyzed: res.suppliers_analyzed || 0,
      });
    } catch (error) {
      console.error("Failed to fetch savings ranking:", error);
      setSavingsMap({});
    } finally {
      setSavingsLoading(false);
    }
  }, [currentRestaurant]);

  const fetchScorecard = useCallback(async () => {
    if (!currentRestaurant?.restaurant_id) return;
    try {
      const res = await api.getSuppliersScorecard(currentRestaurant.restaurant_id, 90);
      setScorecardData(res);
      const map = {};
      (res.items || []).forEach((s) => {
        if (s.supplier_id) map[s.supplier_id] = s;
      });
      setScorecardMap(map);
    } catch (error) {
      console.error("Failed to fetch supplier scorecard:", error);
      setScorecardData(null);
      setScorecardMap({});
    }
  }, [currentRestaurant]);

  useEffect(() => {
    if (!currentRestaurant) return;
    fetchSuppliers();
    fetchSavings();
    fetchScorecard();
  }, [currentRestaurant, fetchSuppliers, fetchSavings, fetchScorecard]);

  const filtered = useMemo(() => {
    const q = normalize(search);
    const list = suppliers.filter(s => {
      if (categoryFilter !== "all" && (s.category_hint || "otros") !== categoryFilter) return false;
      if (onlyEvaluated && !scorecardMap[s.supplier_id]) return false;
      if (!q) return true;
      const hay = [s.name, s.contact_name, s.email, s.phone, s.address, s.notes]
        .map(normalize).join(" ");
      return hay.includes(q);
    });
    const sorted = [...list];
    if (sortBy === "name_asc") sorted.sort((a, b) => normalize(a.name).localeCompare(normalize(b.name)));
    else if (sortBy === "name_desc") sorted.sort((a, b) => normalize(b.name).localeCompare(normalize(a.name)));
    else if (sortBy === "recent") {
      sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    } else if (sortBy === "savings") {
      sorted.sort((a, b) => {
        const sa = savingsMap[a.supplier_id]?.savings || 0;
        const sb = savingsMap[b.supplier_id]?.savings || 0;
        if (sb !== sa) return sb - sa;
        return normalize(a.name).localeCompare(normalize(b.name));
      });
    } else if (sortBy === "grade_best" || sortBy === "grade_worst") {
      const ascending = sortBy === "grade_worst";
      sorted.sort((a, b) => {
        const sa = scorecardMap[a.supplier_id]?.score;
        const sb = scorecardMap[b.supplier_id]?.score;
        // Suppliers without scorecard go to the bottom regardless of direction
        if (sa == null && sb == null) return normalize(a.name).localeCompare(normalize(b.name));
        if (sa == null) return 1;
        if (sb == null) return -1;
        return ascending ? sa - sb : sb - sa;
      });
    }
    return sorted;
  }, [suppliers, search, categoryFilter, sortBy, savingsMap, scorecardMap, onlyEvaluated]);

  // Identify top savers (top 3 with savings > 0) for Trophy badge
  const topSaverIds = useMemo(() => {
    const sorted = Object.values(savingsMap)
      .filter(r => r.savings > 0)
      .sort((a, b) => b.savings - a.savings)
      .slice(0, 3);
    return new Set(sorted.map(r => r.supplier_id));
  }, [savingsMap]);

  const handleSavedSupplier = (saved) => {
    setSuppliers(prev => {
      const idx = prev.findIndex(s => s.supplier_id === saved.supplier_id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [...prev, saved];
    });
  };

  const handleDeletedSupplier = (supplierId) => {
    setSuppliers(prev => prev.filter(s => s.supplier_id !== supplierId));
  };

  const handleBulkCreated = (created) => {
    setSuppliers(prev => [...prev, ...created]);
  };

  if (loading) {
    return <PageSkeleton kpis={3} showTabs={false} rows={6} />;
  }

  const counts = SUPPLIER_CATEGORIES.reduce((acc, c) => {
    acc[c.value] = suppliers.filter(s => (s.category_hint || "otros") === c.value).length;
    return acc;
  }, {});

  const hasAnyRealSavings = Object.values(savingsMap).some(r => r.savings > 0);

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <PageHeader
        icon={<Truck className="w-5 h-5" />}
        title="Proveedores"
        description="Gestiona tus proveedores y pedidos."
        breadcrumb={[{ label: "Operaciones" }, { label: "Proveedores" }]}
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="rounded-full" data-testid="add-supplier-btn">
                <Plus className="w-4 h-4 mr-2" />
                Proveedor
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => { setEditingSupplier(null); setManualDialog(true); }} data-testid="add-supplier-manual">
                <Pencil className="w-4 h-4 mr-2" />
                Crear manualmente
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setAIDialog(true)} data-testid="add-supplier-ai">
                <Sparkles className="w-4 h-4 mr-2 text-violet-600" />
                Sugerir con IA
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      {/* Supplier scorecard banner (basado en pedidos recibidos) */}
      <SupplierScorecardBanner data={scorecardData} />

      {/* Savings insight banner (only when we have real savings data) */}
      {hasAnyRealSavings && savingsMeta && (
        <div
          className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-3 flex items-center gap-3"
          data-testid="savings-insight-banner"
        >
          <div className="shrink-0 h-9 w-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
            <Trophy className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-medium text-emerald-900">
              Ahorro real del trimestre: <span className="font-semibold">{formatCurrency(savingsMeta.total_savings)}</span>
            </div>
            <div className="text-xs text-emerald-700/80">
              Calculado sobre {savingsMeta.ingredients_analyzed} productos con varios proveedores. Ordena por "Ahorro 90d" para verlo en detalle.
            </div>
          </div>
        </div>
      )}

      {/* Filters row */}
      {suppliers.length > 0 && (
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, contacto, email, teléfono o dirección…"
              className="pl-9 h-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="supplier-search-input"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full lg:w-[200px] h-10" data-testid="supplier-category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías ({suppliers.length})</SelectItem>
              {SUPPLIER_CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label} ({counts[c.value] || 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full lg:w-[200px] h-10" data-testid="supplier-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Nombre: A → Z</SelectItem>
              <SelectItem value="name_desc">Nombre: Z → A</SelectItem>
              <SelectItem value="recent">Más recientes</SelectItem>
              <SelectItem value="savings">
                <span className="flex items-center gap-1.5">
                  <TrendingDown className="h-3.5 w-3.5 text-emerald-700" />
                  Ahorro 90d
                </span>
              </SelectItem>
              <SelectItem value="grade_best">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-indigo-600" />
                  Nota: mejor → peor
                </span>
              </SelectItem>
              <SelectItem value="grade_worst">
                <span className="flex items-center gap-1.5">
                  <ShieldAlert className="h-3.5 w-3.5 text-red-700" />
                  Nota: peor → mejor
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          {Object.keys(scorecardMap).length > 0 && (
            <button
              type="button"
              onClick={() => setOnlyEvaluated((v) => !v)}
              className={`h-10 px-3 rounded-md border text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
                onlyEvaluated
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-background hover:bg-muted border-border"
              }`}
              data-testid="supplier-only-evaluated-toggle"
              title="Mostrar solo proveedores con datos de scorecard"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Solo evaluados ({Object.keys(scorecardMap).length})
            </button>
          )}
        </div>
      )}

      {/* Suppliers List */}
      {suppliers.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <Truck className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-heading text-lg font-semibold mb-2">Sin proveedores</h3>
            <p className="text-muted-foreground mb-6">
              Añade proveedores manualmente o déjale a la IA que te los recomiende.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button onClick={() => { setEditingSupplier(null); setManualDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Añadir manualmente
              </Button>
              <Button variant="outline" onClick={() => setAIDialog(true)} className="border-violet-200 text-violet-700 hover:bg-violet-50">
                <Sparkles className="w-4 h-4 mr-2" />
                Sugerir con IA
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">
              Ningún proveedor coincide con los filtros actuales.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="supplier-cards-grid">
          {filtered.map((supplier) => {
            const meta = getCategoryMeta(supplier.category_hint);
            const Icon = meta.Icon;
            const savings = savingsMap[supplier.supplier_id];
            const isTopSaver = topSaverIds.has(supplier.supplier_id);
            const hasSavings = savings && savings.savings > 0;
            const scorecard = scorecardMap[supplier.supplier_id];
            return (
              <Card
                key={supplier.supplier_id}
                className="border-none shadow-soft cursor-pointer hover:shadow-md transition-shadow group"
                onClick={() => { setEditingSupplier(supplier); setManualDialog(true); }}
                data-testid={`supplier-card-${supplier.supplier_id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${meta.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      {scorecard && (
                        <SupplierScorecardChip
                          data={scorecard}
                          compact
                          testId={`supplier-grade-${supplier.supplier_id}`}
                        />
                      )}
                      {isTopSaver && (
                        <span
                          title={`Top ahorro: ${formatCurrency(savings.savings)} en 90d`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-200"
                          data-testid={`supplier-top-saver-${supplier.supplier_id}`}
                        >
                          <Trophy className="h-3 w-3" /> Top ahorro
                        </span>
                      )}
                      <span className={`inline-flex text-[10px] font-medium px-2 py-0.5 rounded-full border ${meta.color}`}>
                        {meta.label}
                      </span>
                      {supplier.auto_suggested && (
                        <span
                          title="Sugerido por IA"
                          className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-100 to-fuchsia-100 text-violet-700 border border-violet-200"
                        >
                          <Sparkles className="h-3 w-3" /> IA
                        </span>
                      )}
                    </div>
                  </div>
                  <CardTitle className="font-heading mt-4 group-hover:text-primary transition-colors">
                    {supplier.name}
                  </CardTitle>
                  {supplier.contact_name && (
                    <p className="text-sm text-muted-foreground">{supplier.contact_name}</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {hasSavings && (
                    <div
                      className="flex items-center gap-2 text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg px-2.5 py-1.5"
                      data-testid={`supplier-savings-${supplier.supplier_id}`}
                    >
                      <TrendingDown className="h-3.5 w-3.5 shrink-0" />
                      <span className="flex-1">
                        <strong>{formatCurrency(savings.savings)}</strong> ahorrados en 90d
                        {savings.cheapest_in > 0 && ` · más barato en ${savings.cheapest_in} producto${savings.cheapest_in === 1 ? "" : "s"}`}
                      </span>
                    </div>
                  )}
                  {supplier.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="truncate">{supplier.email}</span>
                    </div>
                  )}
                  {supplier.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span>{supplier.phone}</span>
                    </div>
                  )}
                  {supplier.address && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="truncate">{supplier.address}</span>
                    </div>
                  )}
                  {supplier.notes && (
                    <p className="text-sm text-muted-foreground pt-2 border-t line-clamp-3">
                      {supplier.notes}
                    </p>
                  )}
                  {scorecard && (scorecard.grade === "D" || scorecard.grade === "C") && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAltSupplier(supplier);
                      }}
                      className="w-full mt-2 inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors"
                      data-testid={`supplier-find-alt-btn-${supplier.supplier_id}`}
                      title="Pedir a la IA 2-3 alternativas reales (2 cr)"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      Buscar alternativa con IA
                    </button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <SupplierDialog
        open={manualDialog}
        onOpenChange={(v) => { setManualDialog(v); if (!v) setEditingSupplier(null); }}
        supplier={editingSupplier}
        restaurantId={currentRestaurant?.restaurant_id}
        onSaved={handleSavedSupplier}
        onDeleted={handleDeletedSupplier}
      />

      <SupplierAIDialog
        open={aiDialog}
        onOpenChange={setAIDialog}
        restaurantId={currentRestaurant?.restaurant_id}
        onCreated={handleBulkCreated}
      />

      <AlternativeSuppliersDialog
        open={!!altSupplier}
        onOpenChange={(o) => { if (!o) setAltSupplier(null); }}
        supplier={altSupplier}
        restaurantId={currentRestaurant?.restaurant_id}
        onCreate={async (alt) => {
          const created = await api.createSupplier(currentRestaurant?.restaurant_id, alt);
          handleSavedSupplier(created);
        }}
      />
    </div>
  );
}
